<template>
  <div class="er" :class="{ 'er--dark': isDark }">
    <!-- STATS ROW (counters first, like /applications) -->
    <section class="er-stats">
      <div class="royal-grid">
        <div class="royal-card royal--all">
          <div class="royal-title">Jami imtihonlar</div>
          <div class="royal-count">{{ summary.total }}</div>
          <div class="royal-meter"><span style="width:100%"></span></div>
        </div>

        <div class="royal-card royal--pass">
          <div class="royal-title">O'tgan</div>
          <div class="royal-count">{{ summary.passed }}</div>
          <div class="royal-meter"><span :style="{ width: (summary.total > 0 ? (summary.passed / summary.total * 100).toFixed(0) : 0) + '%' }"></span></div>
        </div>

        <div class="royal-card royal--fail">
          <div class="royal-title">O'tmagan</div>
          <div class="royal-count">{{ summary.failed }}</div>
          <div class="royal-meter"><span :style="{ width: (summary.total > 0 ? (summary.failed / summary.total * 100).toFixed(0) : 0) + '%' }"></span></div>
        </div>
      </div>

    </section>

    <!-- ── Toolbar: Search (left) + Circle Buttons (right) — copied from /applications ── -->
    <div class="toolbar-row">
      <v-text-field
        :placeholder="$t('Izlash...')"
        append-icon="mdi-magnify"
        v-model="filters.search"
        dense
        hide-details
        outlined
        clearable
        class="search-input"
        @click:append="applyFilters"
        @keyup.enter="applyFilters"
        @click:clear="filters.search = ''; applyFilters()"
      />

      <div class="circle-actions">
        <!-- Result count badge -->
        <v-chip class="badge-count" color="primary" small>
          {{ totalElements || 0 }}
        </v-chip>

        <!-- Filter -->
        <button
          class="filter-btn"
          :class="{ 'filter-btn--dark': isDark }"
          @click="filterOpen = !filterOpen"
        >
          <img src="../../assets/filter.svg" alt="Filter" :class="{ 'filter-icon-dark': isDark }">
        </button>

        <!-- Result-status segment — same component as /payments (All / 1C / Manual). Barchasi = all,
             O'tgan = PASSED, O'tmagan = FAILED. resultStatus matches the row badge exactly. -->
        <div class="seg">
          <button class="seg__btn" :class="{ 'is-active': filters.status === null }" @click="setStatus(null)">Barchasi</button>
          <button class="seg__btn" :class="{ 'is-active': filters.status === 'PASSED' }" @click="setStatus('PASSED')">O'tgan</button>
          <button class="seg__btn" :class="{ 'is-active': filters.status === 'FAILED' }" @click="setStatus('FAILED')">O'tmagan</button>
          <span class="seg__fill" :style="segStyle"></span>
        </div>

        <!-- Export -->
        <v-btn
          v-if="canExport"
          class="modern-export"
          :loading="exporting"
          :disabled="exporting || excelExportDisabled"
          @click="exportExcel"
        >
          <img src="/icons/excel-icon.svg" alt="Excel" class="export-icon">
          EXPORT
        </v-btn>
      </div>
    </div>

    <!-- ── Filter Bar (Fakultet / Ta'lim shakli / Ta'lim tili) — copied from /applications ── -->
    <v-fade-transition>
      <div v-if="filterOpen" class="filter-bar">
        <div class="filter-row">
          <!-- Fakultet -->
          <v-menu v-model="facultyOpen" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on" style="max-width: 274px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                {{ selectedFacultyName || $t('Fakultet') }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: isDark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchFaculty" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="f in filteredFaculties" :key="f.id"
                    :label="f.name" :input-value="filters.facultyId === f.id"
                    hide-details dense class="mr-4 mb-2" style="min-width: 120px;"
                    @change="selectFaculty($event ? f : null)"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="facultyOpen = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Ta'lim shakli -->
          <v-menu v-model="eduTypeOpen" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ selectedEduTypeName || $t("Ta'lim shakli") }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: isDark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchEduType" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="t in filteredEduTypes" :key="t.id"
                    :label="t.name" :input-value="filters.educationTypeId === t.id"
                    hide-details dense class="mr-4 mb-2" style="min-width: 120px;"
                    @change="selectEduType($event ? t : null)"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="eduTypeOpen = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>

          <!-- Ta'lim tili -->
          <v-menu v-model="eduLangOpen" :close-on-content-click="false" offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="button-primary" v-bind="attrs" v-on="on">
                {{ selectedEduLangName || $t("Ta'lim tili") }}
              </v-btn>
            </template>
            <v-card class="filter-card" :style="{ backgroundColor: isDark ? '#1a1b23' : '#FFF' }">
              <v-card-title>
                <v-text-field v-model="searchEduLang" :label="$t('Qidiruv')" clearable hide-details dense />
              </v-card-title>
              <v-divider />
              <v-card-text class="filter-card-body">
                <div class="filter-checks">
                  <v-checkbox
                    v-for="l in filteredEduLangs" :key="l.id"
                    :label="l.name" :input-value="filters.educationLanguageId === l.id"
                    hide-details dense class="mr-4 mb-2" style="min-width: 120px;"
                    @change="selectEduLang($event ? l : null)"
                  />
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn text @click="eduLangOpen = false">{{ $t("Close") }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>
      </div>
    </v-fade-transition>

    <!-- TABLE -->
    <div class="er-card">
      <!-- Skeleton -->
      <template v-if="loading && sessions.length === 0">
        <div v-for="n in 8" :key="'sk'+n" class="er-skel">
          <div class="er-skel__bar" style="width:30px"></div>
          <div class="er-skel__bar" style="width:180px"></div>
          <div class="er-skel__bar" style="width:140px"></div>
          <div class="er-skel__bar" style="width:80px"></div>
          <div class="er-skel__bar" style="width:70px"></div>
          <div class="er-skel__bar" style="width:90px"></div>
          <div class="er-skel__bar" style="width:90px"></div>
        </div>
      </template>

      <!-- Empty -->
      <empty-state
        v-else-if="!loading && sessions.length === 0"
        :title="$t('No results found')"
        :subtitle="$t('Try changing filters or clearing your search')"
      />

      <!-- Data -->
      <table v-else class="er-table">
        <thead>
          <tr>
            <th class="er-th">Talaba</th>
            <th class="er-th">Fakultet</th>
            <th class="er-th">Ta'lim shakli</th>
            <th class="er-th">Ta'lim tili</th>
            <th class="er-th" style="width:130px">Ball</th>
            <th class="er-th" style="width:110px">Holat</th>
            <th class="er-th" style="width:150px">Sana</th>
            <th class="er-th" style="width:120px">Amallar</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in sessions" :key="item.sessionId" class="er-row">
            <td class="er-td">
              <div class="er-student">
                <div class="er-student__avatar">{{ initials(item.fullName) }}</div>
                <div>
                  <div class="er-student__name">{{ item.fullName }}</div>
                  <div class="er-student__phone">{{ item.mobilePhone || '' }}</div>
                </div>
              </div>
            </td>
            <td class="er-td er-td--faculty">{{ item.facultyName }}</td>
            <td class="er-td er-td--faculty">{{ item.educationTypeName || '' }}</td>
            <td class="er-td er-td--faculty">{{ item.educationLanguageName || '' }}</td>
            <td class="er-td">
              <div class="er-score">
                <template v-if="isFinished(item)">
                  <span class="er-score__val">{{ item.totalScore || 0 }}</span>
                  <span class="er-score__max">/{{ item.maxScore || 204 }}</span>
                </template>
                <span v-else class="er-score__val er-score__val--na">—</span>
              </div>
              <div class="er-bar" v-if="isFinished(item)">
                <div class="er-bar__fill" :class="item.passed ? 'er-bar--pass' : 'er-bar--fail'" :style="{ width: scorePercent(item) + '%' }"></div>
              </div>
            </td>
            <td class="er-td">
              <span class="er-badge" :class="badgeClass(item)">{{ badgeText(item) }}</span>
            </td>
            <td class="er-td er-td--date">{{ formatDate(item.startedAt) }}</td>
            <td class="er-td">
              <div class="er-actions">
                <button class="er-act er-act--view" title="Ko'rish" @click="openDetail(item)">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
                </button>
                <button v-if="canUpdate" class="er-act er-act--edit" title="Tahrirlash" @click="openEdit(item)">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>
                </button>
                <button v-if="canDelete" class="er-act er-act--delete" title="O'chirish" @click="confirmDelete(item)">
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/></svg>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="er-pagination">
        <v-pagination
          v-model="page"
          :length="totalPages"
          :total-visible="7"
        />
      </div>
    </div>

    <!-- DETAIL MODAL -->
    <transition name="er-modal">
      <div v-if="detailDialog" class="er-overlay" @mousedown.self="detailDialog = false">
        <div class="er-modal er-modal--detail" id="er-print-root">
          <div class="er-modal__header">
            <div>
              <h3 class="er-modal__title">Imtihon natijalari</h3>
              <p class="er-modal__sub" v-if="detail">{{ detail.fullName }} &middot; {{ detail.facultyName }}</p>
            </div>
            <div class="er-modal__header-right" v-if="detail">
              <div class="er-score-big">
                <template v-if="isFinished(detail)">
                  <span class="er-score-big__val">{{ detail.totalScore || 0 }}</span>
                  <span class="er-score-big__max">/{{ detail.maxScore || 204 }}</span>
                </template>
                <span v-else class="er-score-big__val er-score-big__val--na">—</span>
              </div>
              <span class="er-badge ml-3" :class="badgeClass(detail)">{{ badgeText(detail) }}</span>
              <span class="er-detail-stat">{{ detail.correctCount || 0 }}/{{ detail.totalQuestions || 0 }} to'g'ri</span>
            </div>
          </div>

          <div class="er-modal__body" v-if="detail && detail.questions">
            <div class="er-modal__toolbar">
              <button class="er-print-btn" @click="printDetail" type="button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
                  <polyline points="6 9 6 2 18 2 18 9"/>
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                  <rect x="6" y="14" width="12" height="8"/>
                </svg>
                <span>Chop etish</span>
              </button>
              <button class="er-close-btn er-no-print" @click="detailDialog = false" type="button" title="Yopish" aria-label="Yopish">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
              </button>
            </div>
            <div v-if="scienceBreakdown.length" class="er-science-summary">
              <div class="er-science-summary__title">Fanlar bo'yicha natija</div>
              <div class="er-science-summary__grid">
                <div v-for="s in scienceBreakdown" :key="s.name" class="er-science-card">
                  <div class="er-science-card__name" :title="s.name">{{ s.name }}</div>
                  <div class="er-science-card__row">
                    <span class="er-science-card__correct">{{ s.correct }}</span>
                    <span class="er-science-card__sep">/</span>
                    <span class="er-science-card__total">{{ s.total }}</span>
                    <span class="er-science-card__label">to'g'ri</span>
                  </div>
                  <div class="er-science-card__score">
                    <span class="er-science-card__score-val">{{ s.score }}</span>
                    <span class="er-science-card__score-max">/{{ s.maxScore }} ball</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-for="(q, qi) in detail.questions" :key="q.questionId" class="er-q" :class="q.isCorrect ? 'er-q--correct' : 'er-q--wrong'">
              <div class="er-q__head">
                <span class="er-q__num">{{ qi + 1 }}</span>
                <span class="er-q__status">
                  <svg v-if="q.isCorrect" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  <svg v-else viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </span>
                <span v-if="q.scienceName" class="er-q__science">{{ q.scienceName }}</span>
              </div>
              <div class="er-q__body">{{ q.questionBody }}</div>
              <div class="er-q__answers">
                <div v-for="(a, ai) in q.answers" :key="a.answerId" class="er-a" :class="{
                  'er-a--selected': a.answerId === q.selectedAnswerId,
                  'er-a--correct': a.isCorrect,
                  'er-a--wrong': a.answerId === q.selectedAnswerId && !a.isCorrect
                }">
                  <span class="er-a__letter">{{ String.fromCharCode(65 + ai) }}</span>
                  <span class="er-a__text">{{ a.value }}</span>
                  <svg v-if="a.isCorrect" class="er-a__icon er-a__icon--correct" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                  <svg v-else-if="a.answerId === q.selectedAnswerId" class="er-a__icon er-a__icon--wrong" viewBox="0 0 20 20" fill="currentColor" width="16" height="16"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="er-modal__loading">
            <div class="er-spinner"></div>
          </div>
        </div>
      </div>
    </transition>

    <!-- EDIT MODAL -->
    <transition name="er-modal">
      <div v-if="editDialog" class="er-overlay" @mousedown.self="editDialog = false">
        <div class="er-modal er-modal--sm">
          <div class="er-modal__header">
            <h3 class="er-modal__title">Ballni tahrirlash</h3>
            <button class="er-modal__close" @click="editDialog = false">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
            </button>
          </div>
          <div class="er-modal__body">
            <p class="er-edit-name">{{ editTarget?.fullName }}</p>
            <label class="er-field">
              <span class="er-field__label">Ball (max 204)</span>
              <input v-model.number="editForm.totalScore" type="number" min="0" max="204" step="0.1" class="er-field__input" />
            </label>
            <label class="er-toggle">
              <input type="checkbox" v-model="editForm.passed" class="er-toggle__input" />
              <span class="er-toggle__track"><span class="er-toggle__thumb"></span></span>
              <span class="er-toggle__label">O'tgan</span>
            </label>
          </div>
          <div class="er-modal__footer">
            <button class="er-btn er-btn--ghost" @click="editDialog = false">Bekor qilish</button>
            <button class="er-btn er-btn--primary" @click="saveScore" :disabled="saving">
              <span v-if="saving" class="er-spinner er-spinner--sm"></span>
              Saqlash
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- DELETE MODAL -->
    <transition name="er-modal">
      <div v-if="deleteDialog" class="er-overlay" @mousedown.self="deleteDialog = false">
        <div class="er-modal er-modal--sm">
          <div class="er-modal__header">
            <h3 class="er-modal__title">Sessiyani o'chirish</h3>
            <button class="er-modal__close" @click="deleteDialog = false">
              <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
            </button>
          </div>
          <div class="er-modal__body">
            <p><strong>{{ deleteTarget?.fullName }}</strong> ning imtihon sessiyasini o'chirishni tasdiqlaysizmi?</p>
            <p class="er-caption">Bu amalni qaytarib bo'lmaydi.</p>
          </div>
          <div class="er-modal__footer">
            <button class="er-btn er-btn--ghost" @click="deleteDialog = false">Bekor qilish</button>
            <button class="er-btn er-btn--danger" @click="doDelete" :disabled="deleting">
              <span v-if="deleting" class="er-spinner er-spinner--sm"></span>
              O'chirish
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import axios from "axios";
import EmptyState from "@/components/EmptyState.vue";

export default {
  name: "AdminExamResults",
  components: { EmptyState },
  data: () => ({
    loading: false,
    canCreate: false,
    canUpdate: false,
    canDelete: false,
    canExport: false,
    filters: { search: "", facultyId: null, educationTypeId: null, educationLanguageId: null, status: null, page: 0, size: 20 },
    filterOpen: false,
    facultyOpen: false,
    eduTypeOpen: false,
    eduLangOpen: false,
    searchFaculty: "",
    searchEduType: "",
    searchEduLang: "",
    detailDialog: false,
    editDialog: false,
    deleteDialog: false,
    detail: null,
    editForm: { totalScore: 0, passed: false },
    editTarget: null,
    deleteTarget: null,
    saving: false,
    deleting: false,
    exporting: false,
    debouncer: null,
  }),
  computed: {
    ...mapGetters("admin-exam-results", ["GET_SESSIONS", "GET_TOTAL_ELEMENTS", "GET_SUMMARY", "GET_FACULTIES", "GET_EDUCATION_TYPES", "GET_EDUCATION_LANGUAGES"]),
    sessions() { return this.GET_SESSIONS; },
    totalElements() { return this.GET_TOTAL_ELEMENTS; },
    // Real totals for the cards (from /summary), independent of the active tab and page.
    summary() { return this.GET_SUMMARY; },
    faculties() { return this.GET_FACULTIES; },
    educationTypes() { return this.GET_EDUCATION_TYPES; },
    educationLanguages() { return this.GET_EDUCATION_LANGUAGES; },
    // Filter-chip option lists filtered by the per-chip "Qidiruv" search box (mirrors /applications).
    filteredFaculties() {
      const s = (this.searchFaculty || "").toLowerCase();
      return (this.faculties || []).filter(o => (o.name || "").toLowerCase().includes(s));
    },
    filteredEduTypes() {
      const s = (this.searchEduType || "").toLowerCase();
      return (this.educationTypes || []).filter(o => (o.name || "").toLowerCase().includes(s));
    },
    filteredEduLangs() {
      const s = (this.searchEduLang || "").toLowerCase();
      return (this.educationLanguages || []).filter(o => (o.name || "").toLowerCase().includes(s));
    },
    page: {
      get() { return this.filters.page + 1; },
      set(v) { this.filters.page = v - 1; this.fetchAll(); },
    },
    totalPages() { return Math.max(1, Math.ceil(this.totalElements / this.filters.size)); },
    isDark() { return this.$vuetify && this.$vuetify.theme.dark; },
    selectedFacultyName() {
      if (!this.filters.facultyId) return null;
      const f = this.faculties.find(x => x.id === this.filters.facultyId);
      return f ? f.name : null;
    },
    selectedEduTypeName() {
      if (!this.filters.educationTypeId) return null;
      const t = this.educationTypes.find(x => x.id === this.filters.educationTypeId);
      return t ? t.name : null;
    },
    selectedEduLangName() {
      if (!this.filters.educationLanguageId) return null;
      const l = this.educationLanguages.find(x => x.id === this.filters.educationLanguageId);
      return l ? l.name : null;
    },
    // Sliding pill for the .seg control, positioned like /payments (left-based, 3 equal slots).
    segStyle() {
      const base = { left: "2px" };
      if (this.filters.status === "PASSED") base.left = "calc((100% - 4px) / 3 + 2px)";
      if (this.filters.status === "FAILED") base.left = "calc((100% - 4px) / 3 * 2 + 2px)";
      return base;
    },
    scienceBreakdown() {
      if (!this.detail || !Array.isArray(this.detail.questions) || !this.detail.questions.length) {
        return [];
      }
      const totalQs = this.detail.totalQuestions || this.detail.questions.length;
      const maxScore = Number(this.detail.maxScore) || 0;
      const totalScore = Number(this.detail.totalScore) || 0;
      const pointPerQ = totalQs > 0 ? maxScore / totalQs : 0;

      const map = new Map();
      for (const q of this.detail.questions) {
        const name = q.scienceName || "—";
        if (!map.has(name)) map.set(name, { name, correct: 0, total: 0 });
        const e = map.get(name);
        e.total += 1;
        if (q.isCorrect) e.correct += 1;
      }

      const list = Array.from(map.values()).map(e => ({
        ...e,
        maxScore: Math.round(e.total * pointPerQ * 10) / 10,
        score: Math.round(e.correct * pointPerQ * 10) / 10,
      }));

      // Reconcile rounding so per-science scores sum exactly to totalScore.
      const sum = list.reduce((acc, s) => acc + s.score, 0);
      const diff = Math.round((totalScore - sum) * 10) / 10;
      if (diff !== 0 && list.length) {
        const target = list.reduce((a, b) => (b.correct >= a.correct ? b : a));
        target.score = Math.round((target.score + diff) * 10) / 10;
      }
      return list;
    },
  },
  watch: {
    "filters.search"() {
      clearTimeout(this.debouncer);
      this.debouncer = setTimeout(() => this.applyFilters(), 300);
    },
  },
  created() {
    this.fetchAll();
    this.FETCH_FACULTIES();
    this.FETCH_EDUCATION_TYPES();
    this.FETCH_EDUCATION_LANGUAGES();
    document.addEventListener("click", this.closeDropdowns);
  },
  async mounted() {
    this.canCreate = await this.hasRole("IMTIHON_NATIJALARI", "CREATE");
    this.canUpdate = await this.hasRole("IMTIHON_NATIJALARI", "UPDATE");
    this.canDelete = await this.hasRole("IMTIHON_NATIJALARI", "DELETE");
    this.canExport = await this.hasRole("IMTIHON_NATIJALARI", "EXCEL");
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdowns);
  },
  methods: {
    ...mapActions("admin-exam-results", ["FETCH_SESSIONS", "FETCH_SUMMARY", "FETCH_DETAIL", "UPDATE_SCORE", "DELETE_SESSION", "FETCH_FACULTIES", "FETCH_EDUCATION_TYPES", "FETCH_EDUCATION_LANGUAGES", "EXPORT_EXCEL"]),
    // Fetch the filtered page AND the real totals together (like /payments' fetchAll). loading is
    // always cleared in finally, so the controls can never freeze; the store's request-sequence guard
    // keeps the latest tab/page the one that wins under rapid clicks.
    async fetchAll() {
      this.loading = true;
      const commonFilters = {
        facultyId: this.filters.facultyId,
        educationTypeId: this.filters.educationTypeId,
        educationLanguageId: this.filters.educationLanguageId,
        search: this.filters.search || null,
      };
      try {
        await Promise.all([
          this.FETCH_SESSIONS({
            page: this.filters.page,
            size: this.filters.size,
            resultStatus: this.filters.status,
            ...commonFilters,
          }),
          this.FETCH_SUMMARY(commonFilters),
        ]);
      } finally {
        this.loading = false;
      }
    },
    applyFilters() { this.filters.page = 0; this.fetchAll(); },
    setStatus(val) { this.filters.status = val; this.applyFilters(); },
    selectFaculty(f) {
      this.filters.facultyId = f ? f.id : null;
      this.facultyOpen = false;
      this.applyFilters();
    },
    selectEduType(t) {
      this.filters.educationTypeId = t ? t.id : null;
      this.eduTypeOpen = false;
      this.applyFilters();
    },
    selectEduLang(l) {
      this.filters.educationLanguageId = l ? l.id : null;
      this.eduLangOpen = false;
      this.applyFilters();
    },
    closeDropdowns(e) {
      if (this.$refs.facultyDrop && !this.$refs.facultyDrop.contains(e.target)) {
        this.facultyOpen = false;
      }
      if (this.$refs.eduTypeDrop && !this.$refs.eduTypeDrop.contains(e.target)) {
        this.eduTypeOpen = false;
      }
      if (this.$refs.eduLangDrop && !this.$refs.eduLangDrop.contains(e.target)) {
        this.eduLangOpen = false;
      }
    },
    isFinished(item) {
      // Only a submitted exam has a result. IN_PROGRESS sessions (a live or abandoned attempt) are
      // shown as "Jarayonda" with a "—" score instead of a misleading 0 / O'tmagan.
      return !!item && item.status === "FINISHED";
    },
    badgeText(item) {
      if (!this.isFinished(item)) return "Jarayonda";
      return item.passed ? "O'tgan" : "O'tmagan";
    },
    badgeClass(item) {
      if (!this.isFinished(item)) return "er-badge--progress";
      return item.passed ? "er-badge--pass" : "er-badge--fail";
    },
    scorePercent(item) {
      return item.maxScore > 0 ? Math.round((item.totalScore || 0) / item.maxScore * 100) : 0;
    },
    initials(name) {
      if (!name) return "?";
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      return parts[0] ? parts[0][0].toUpperCase() : "?";
    },
    formatDate(iso) {
      if (!iso) return "—";
      const d = new Date(iso);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      return `${dd}.${mm}.${yyyy} ${hh}:${mi}`;
    },
    async openDetail(item) {
      this.detail = null;
      this.detailDialog = true;
      this.detail = await this.FETCH_DETAIL({ sessionId: item.sessionId });
    },
    openEdit(item) {
      this.editTarget = item;
      this.editForm = { totalScore: item.totalScore || 0, passed: !!item.passed };
      this.editDialog = true;
    },
    async saveScore() {
      this.saving = true;
      const ok = await this.UPDATE_SCORE({
        sessionId: this.editTarget.sessionId,
        totalScore: this.editForm.totalScore,
        passed: this.editForm.passed,
      });
      this.saving = false;
      if (ok) {
        this.editDialog = false;
        this.fetchAll();
      }
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async exportExcel() {
      this.exporting = true;
      await this.EXPORT_EXCEL({
        resultStatus: this.filters.status,
        facultyId: this.filters.facultyId,
        educationTypeId: this.filters.educationTypeId,
        educationLanguageId: this.filters.educationLanguageId,
        search: this.filters.search || null,
      });
      this.exporting = false;
    },
    async doDelete() {
      this.deleting = true;
      const ok = await this.DELETE_SESSION({ sessionId: this.deleteTarget.sessionId });
      this.deleting = false;
      if (ok) {
        this.deleteDialog = false;
        this.fetchAll();
      }
    },
    printDetail() {
      if (!this.detail) return;
      const d = this.detail;
      const esc = (s) => String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

      const sciencesHtml = (this.scienceBreakdown || []).map((s) => `
        <div class="sci">
          <div class="sci-name">${esc(s.name)}</div>
          <div class="sci-row"><span class="sci-correct">${s.correct}</span>
            <span class="sci-sep">/</span><span class="sci-total">${s.total}</span>
            <span class="sci-label">to'g'ri</span></div>
          <div class="sci-score"><b>${s.score}</b>/${s.maxScore} ball</div>
        </div>`).join("");

      const questionsHtml = (d.questions || []).map((q, qi) => {
        const answers = (q.answers || []).map((a, ai) => {
          let cls = "ans";
          if (a.isCorrect) cls += " ans-correct";
          if (a.answerId === q.selectedAnswerId && !a.isCorrect) cls += " ans-wrong";
          if (a.answerId === q.selectedAnswerId) cls += " ans-selected";
          return `<div class="${cls}"><span class="letter">${String.fromCharCode(65 + ai)}</span>
            <span class="val">${esc(a.value)}</span></div>`;
        }).join("");
        const status = q.isCorrect ? "✓" : "✗";
        const statusCls = q.isCorrect ? "q-ok" : "q-bad";
        return `<div class="q ${statusCls}">
          <div class="q-head">
            <span class="q-num">${qi + 1}</span>
            <span class="q-status">${status}</span>
            ${q.scienceName ? `<span class="q-sci">${esc(q.scienceName)}</span>` : ""}
          </div>
          <div class="q-body">${esc(q.questionBody)}</div>
          <div class="q-answers">${answers}</div>
        </div>`;
      }).join("");

      const html = `<!doctype html><html lang="uz"><head><meta charset="utf-8">
<title>${esc(d.fullName || "")} — Imtihon natijalari</title>
<style>
  *, *::before, *::after { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 0; padding: 24px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; padding-bottom: 14px; border-bottom: 1px solid #ddd; margin-bottom: 18px; }
  .head h1 { margin: 0 0 4px; font-size: 20px; }
  .head .sub { color: #555; font-size: 13px; }
  .head .right { text-align: right; }
  .score { font-size: 26px; font-weight: 800; letter-spacing: -.02em; }
  .score .max { font-size: 14px; color: #777; font-weight: 500; }
  .badge { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; margin-left: 8px; }
  .badge-pass { background: #ecfdf5; color: #065f46; }
  .badge-fail { background: #fef2f2; color: #991b1b; }
  .badge-progress { background: #eff6ff; color: #1e40af; }
  .stat { display: inline-block; margin-left: 8px; padding: 3px 10px; background: #f1f5f9; border-radius: 8px; font-size: 12px; color: #475569; }
  .sci-summary { padding: 14px 16px; border: 1px solid #e2e8f0; border-radius: 10px; margin-bottom: 18px; background: #fafafa; page-break-inside: avoid; }
  .sci-summary h2 { margin: 0 0 12px; font-size: 12px; text-transform: uppercase; letter-spacing: .05em; color: #64748b; }
  .sci-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 8px; }
  .sci { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
  .sci-name { font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .sci-row { font-size: 13px; margin-bottom: 4px; }
  .sci-correct { font-size: 18px; font-weight: 800; }
  .sci-sep, .sci-total { color: #777; }
  .sci-label { margin-left: 4px; font-size: 11px; color: #777; }
  .sci-score { font-size: 12px; color: #475569; }
  .q { padding: 12px 14px; margin-bottom: 10px; border: 1px solid #e2e8f0; border-radius: 8px; border-left: 3px solid #ccc; page-break-inside: avoid; }
  .q.q-ok { border-left-color: #10b981; }
  .q.q-bad { border-left-color: #ef4444; }
  .q-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 12px; color: #475569; }
  .q-num { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 22px; padding: 0 6px; border-radius: 6px; background: #f1f5f9; font-weight: 700; color: #111; }
  .q-status { font-weight: 800; }
  .q-ok .q-status { color: #10b981; }
  .q-bad .q-status { color: #ef4444; }
  .q-sci { margin-left: auto; padding: 2px 8px; background: #fef3c7; border-radius: 999px; font-size: 11px; color: #92400e; font-weight: 600; }
  .q-body { font-size: 13px; line-height: 1.4; margin-bottom: 8px; white-space: pre-wrap; }
  .q-answers { display: flex; flex-direction: column; gap: 4px; }
  .ans { display: flex; align-items: center; gap: 8px; padding: 6px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 12px; }
  .ans .letter { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 4px; background: #f1f5f9; font-weight: 700; font-size: 11px; }
  .ans-correct { background: #ecfdf5; border-color: #10b981; }
  .ans-correct .letter { background: #10b981; color: #fff; }
  .ans-wrong { background: #fef2f2; border-color: #ef4444; }
  .ans-wrong .letter { background: #ef4444; color: #fff; }
  @page { margin: 14mm 10mm; }
</style></head><body>
<div class="head">
  <div>
    <h1>Imtihon natijalari</h1>
    <div class="sub">${esc(d.fullName || "")}${d.facultyName ? " · " + esc(d.facultyName) : ""}</div>
  </div>
  <div class="right">
    <div class="score">${d.status === "FINISHED" ? (d.totalScore || 0) + `<span class="max">/${d.maxScore || 204}</span>` : "—"}</div>
    <span class="badge ${d.status !== "FINISHED" ? "badge-progress" : (d.passed ? "badge-pass" : "badge-fail")}">${d.status !== "FINISHED" ? "Jarayonda" : (d.passed ? "O'tgan" : "O'tmagan")}</span>
    <span class="stat">${d.correctCount || 0}/${d.totalQuestions || 0} to'g'ri</span>
  </div>
</div>
${sciencesHtml ? `<section class="sci-summary"><h2>Fanlar bo'yicha natija</h2><div class="sci-grid">${sciencesHtml}</div></section>` : ""}
${questionsHtml}
<` + `script>
  window.onload = function () { setTimeout(function () { window.print(); }, 50); };
  window.onafterprint = function () { window.close(); };
<` + `/script>
</body></html>`;

      const w = window.open("", "_blank", "width=900,height=1100");
      if (!w) {
        this.$store.dispatch("SET_SNACKBAR", { text: "Pop-up bloklangan. Iltimos, ushbu sayt uchun pop-up'larga ruxsat bering.", color: "error" });
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
    },

    async hasRole(panel, op) {
      try {
        const res = await axios.get(`/admin/panel-permissions/check`, {
          params: { appPanel: panel, op }
        });
        return res.data === true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ══════════════════════════════════════════════
   DESIGN TOKENS
   ══════════════════════════════════════════════ */
.er {
  --bg: #ffffff;
  --card: #fff;
  --card-hover: #fafbfc;
  --border: rgba(0,0,0,.06);
  --text: #0f172a;
  --text2: #475569;
  --muted: #94a3b8;
  --accent: #ffd451;
  --accent-hover: #f5c542;
  --pass: #16a34a;
  --pass-bg: rgba(22,163,74,.08);
  --fail: #dc2626;
  --fail-bg: rgba(220,38,38,.06);
  --progress: #2563eb;
  --progress-bg: rgba(37,99,235,.08);
  --radius: 14px;
  --radius-sm: 10px;
  --shadow: 0 1px 3px rgba(0,0,0,.04), 0 4px 14px rgba(0,0,0,.03);
  --nav-blur: blur(18px) saturate(1.6);

  min-height: 100vh;
  padding: 0 24px 40px;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif;
}

.er--dark {
  --bg: #111318;
  --card: #1a1b23;
  --card-hover: #22232e;
  --border: rgba(255,255,255,.06);
  --text: #e4e5eb;
  --text2: #9ca3af;
  --muted: #606275;
  --pass: #4ade80;
  --pass-bg: rgba(74,222,128,.08);
  --fail: #f87171;
  --fail-bg: rgba(248,113,113,.06);
  --progress: #60a5fa;
  --progress-bg: rgba(96,165,250,.10);
  --shadow: 0 2px 8px rgba(0,0,0,.3);
}

/* ══════ NAV ══════ */
.er-nav {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  margin: 0 0 20px;
}


.er-nav__left { display: flex; align-items: center; gap: 10px; flex: 1; }
.er-nav__right { display: flex; align-items: center; gap: 12px; }

/* ── Filter funnel toggle (reveals the Fakultet / Ta'lim shakli / Ta'lim tili row) ── */
.er-filter-toggle {
  display: inline-flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; flex-shrink: 0;
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); color: var(--text);
  cursor: pointer; transition: border-color .15s, color .15s, background .15s;
}
.er-filter-toggle:hover { border-color: var(--accent); color: var(--accent); }
.er-filter-toggle.is-active { border-color: var(--accent); color: var(--accent); background: rgba(255,212,81,.12); }

/* ── Filter row revealed by the funnel ── */
.er-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 0 0 20px; }

/* ── Toolbar + filter — copied verbatim from /applications (AdminApplicationsList.vue) ── */
.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 20px 12px;
}
.toolbar-row > .search-input { flex: 1; min-width: 0; }
.circle-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  max-width: 100%;
  flex-wrap: wrap;
  row-gap: 10px;
}
.circle-actions > * { flex-shrink: 0; }
.badge-count { font-size: 12px !important; padding: 4px 10px !important; font-weight: 600 !important; }
.badge-count.primary { color: white !important; }
.theme--dark .badge-count.primary { color: #111318 !important; }
.filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  width: 40px;
  height: 40px;
  min-width: 40px;
  max-width: 40px;
  padding: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid #d0d5dd;
  outline: none;
}
.filter-btn img { width: 18px; height: 18px; object-fit: cover; }
.filter-btn:hover { background-color: #e8e8e8; }
.filter-btn:active { background-color: #ddd; transform: scale(0.97); }
.filter-btn--dark { background-color: #22232e; border-color: #33354a; }
.filter-btn--dark:hover { background-color: #2a2c3a; }
.filter-btn--dark:active { background-color: #33354a; }
.filter-icon-dark { filter: brightness(0) invert(1); opacity: 0.7; }
.modern-export {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 10px !important;
  background: #217346 !important;
  color: #fff !important;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600 !important;
  letter-spacing: .03em;
  text-transform: none !important;
  transition: all .2s ease;
  height: 36px !important;
  min-width: 130px !important;
}
.modern-export:hover { background: #1a5c38 !important; }
.modern-export:active { transform: scale(.97); }
.export-icon { width: 18px; height: 18px; filter: brightness(0) invert(1); margin-right: 10px; }
.filter-bar { padding: 12px 16px; border-bottom: 1px solid #e6e8ec; }
.theme--dark .filter-bar { border-bottom-color: #2a2c3a; }
.filter-row { display: flex; flex-wrap: wrap; gap: 12px; }
.button-primary {
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 10px;
  transition: all 0.15s ease-in-out;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  text-transform: none;
}
.button-primary:hover { background-color: #f9fafb; border-color: #c0c7d0; }
.button-primary:active { background-color: #f0f2f5; box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05); }
.button-primary--selected { background-color: #2e90fa; border-color: #2e90fa; color: #ffffff; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05); }
.button-primary.theme--dark { background-color: #22232e !important; color: #d5d6de !important; border-color: #33354a !important; }
.button-primary.theme--dark:hover { background-color: #2a2c3a !important; }
.filter-card { max-width: 300px; max-height: 400px; }
.filter-card-body { max-height: 260px; overflow-y: auto; }
.filter-checks { display: flex; flex-wrap: wrap; }

.er-nav__progress {
  position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, var(--accent) 30%, transparent 30%);
  animation: er-progress 1s ease infinite;
}
@keyframes er-progress {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* ── Search ── */
::v-deep .search-input {
  .v-input__slot {
    background: #f1f5f9 !important;
    border-radius: 10px !important;
    min-height: 38px !important;
  }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #d0d5dd !important; }
  &.v-input--is-focused fieldset { border-color: #00274c !important; border-width: 1.5px !important; }
  .v-icon { color: #94a3b8 !important; font-size: 20px !important; }
  input { font-size: 14px; }
  input::placeholder { color: #94a3b8 !important; }
}
::v-deep .search-input.theme--dark {
  .v-input__slot { background: #22232e !important; }
  fieldset { border-color: transparent !important; }
  &:hover fieldset { border-color: #33354a !important; }
  &.v-input--is-focused fieldset { border-color: #ffd451 !important; }
  .v-icon { color: #606275 !important; }
  input { color: #d5d6de !important; }
  input::placeholder { color: #606275 !important; }
}

/* ── Faculty Select ── */
.er-select { position: relative; }
.er-select__trigger {
  display: flex; align-items: center; gap: 6px;
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); padding: 9px 14px;
  font-size: 13px; color: var(--text); cursor: pointer;
  white-space: nowrap; transition: border-color .15s;
}
.er-select__trigger:hover { border-color: var(--accent); }
.er-select__chevron { transition: transform .2s; }
.er-select__chevron.is-open { transform: rotate(180deg); }
.er-select__dropdown {
  position: absolute; top: calc(100% + 6px); left: 0; min-width: 240px;
  background: var(--card); border: 1px solid var(--border);
  border-radius: var(--radius-sm); box-shadow: var(--shadow);
  max-height: 280px; overflow-y: auto; z-index: 30;
}
.er-select__option {
  display: block; width: 100%; text-align: left; border: none; background: none;
  padding: 10px 16px; font-size: 13px; color: var(--text); cursor: pointer;
  transition: background .1s;
}
.er-select__option:hover { background: var(--card-hover); }
.er-select__option.is-active { background: rgba(255,212,81,.12); color: var(--accent); font-weight: 600; }

.er-drop-enter-active, .er-drop-leave-active { transition: all .15s ease; }
.er-drop-enter, .er-drop-leave-to { opacity: 0; transform: translateY(-6px); }

/* ── Segmented ── */
.er-seg {
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  overflow: hidden;
  min-width: 240px;
}
.er-seg__btn {
  position: relative; z-index: 2;
  border: none; background: none; padding: 9px 16px;
  font-size: 13px; font-weight: 500; color: var(--text2);
  cursor: pointer; transition: color .2s;
  white-space: nowrap;
}
.er-seg__btn.is-active { color: #000; }
.er-seg__fill {
  position: absolute; top: 2px; bottom: 2px; left: 2px;
  width: calc(33.333% - 2px); border-radius: 8px;
  background: var(--accent);
  transition: transform .25s cubic-bezier(.4,0,.2,1);
  z-index: 1;
}

/* Result-status segment — the same pill control as /payments (All / 1C / Manual). */
.seg {
  position: relative;
  display: grid; grid-template-columns: repeat(3, 1fr);
  background: #f1f5f9; border: 1px solid #e2e8f0;
  border-radius: 999px; padding: 2px; height: 34px; min-width: 200px; width: 230px;
  align-items: center; overflow: hidden;
  flex-shrink: 0;
}
.seg__btn {
  z-index: 2; appearance: none; border: 0; background: transparent;
  padding: 4px 10px; border-radius: 999px; font-weight: 700; font-size: 12px;
  color: #475569; cursor: pointer; text-align: center;
}
.seg__btn.is-active { color: #000; }
.seg__fill {
  position: absolute; top: 2px; bottom: 2px;
  width: calc((100% - 4px) / 3);
  border-radius: 999px; background: #ffd451;
  transition: left .18s ease;
}
.er--dark .seg { background: #22232e; border-color: rgba(255,255,255,.06); }
.er--dark .seg__btn { color: #8b8d9e; }
.er--dark .seg__btn.is-active { color: #000 !important; }

/* ══════ STATS ══════ */
.er-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}

/* ── Royal Cards (copied from admin-account2025) ── */
$royal-radius: 14px;
$royal-line: #e6e8ec;
$royal-ink: #0f172a;
$royal-muted: #667085;
$royal-blue: #3451e6;
$royal-pass: #148b4b;
$royal-fail: #c81e1e;

.royal-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  flex: 1;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
}

.royal-card {
  position: relative;
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: $royal-radius;
  padding: 16px;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  transition: border-color .18s ease, transform .18s ease, background-color .18s ease;
  outline: none;

  &:hover  { transform: translateY(-1px); border-color: #d7d9df; }
  &:active { transform: translateY(0); }

  &::after {
    content: "";
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0,0,0,.03), transparent);
    transform: translateX(-100%); pointer-events: none;
  }
  &:hover::after { animation: royal-wave 1200ms ease; }

  animation: royal-slideUpFade .38s ease both;
}
.royal-card:nth-child(1){ animation-delay: 40ms; }
.royal-card:nth-child(2){ animation-delay: 80ms; }
.royal-card:nth-child(3){ animation-delay: 120ms; }

.royal-title {
  color: $royal-muted;
  font-size: 12px; line-height: 1;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-weight: 700;
}
.er--dark .royal-title { color: var(--muted); }

.royal-count {
  color: $royal-ink;
  font-weight: 900;
  font-size: clamp(24px, 3.6vw, 36px);
  line-height: 1.05;
  letter-spacing: -0.2px;
  font-variant-numeric: tabular-nums lining-nums;
}
.er--dark .royal-count { color: var(--text); }

.royal-meter {
  height: 6px; margin-top: 6px;
  border-radius: 999px; background: #f1f3f5; overflow: hidden;
  > span { display: block; height: 100%; width: 0%; transition: width .45s cubic-bezier(.2,.8,.2,1); }
}
.er--dark .royal-meter { background: var(--border); }

.royal-card.royal--all  { .royal-meter > span { background: $royal-blue; } }
.royal-card.royal--pass { .royal-meter > span { background: $royal-pass; } }
.royal-card.royal--fail { .royal-meter > span { background: $royal-fail; } }

@keyframes royal-wave {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
@keyframes royal-slideUpFade {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .royal-card,
  .royal-meter > span { transition: none; animation: none; }
}

/* ── Pagination (same as admin-exam-questions) ── */
.er-pagination {
  display: flex;
  justify-content: center;
  padding: 16px 20px;
  border-top: 1px solid var(--border);

  &::v-deep .v-pagination__item {
    border: 1px solid #d0d5dd;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    font-weight: 500;
    font-size: 14px;
    border-radius: 8px;
  }
  &::v-deep .v-pagination__item--active {
    background: #ffd451 !important;
    border-color: #f5c542 !important;
    color: #1a1500 !important;
  }
  &::v-deep .v-pagination__navigation {
    border: 1px solid #d0d5dd;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
    border-radius: 8px;
  }
}

.er--dark .er-pagination {
  border-top-color: #2a2c3a;

  &::v-deep .v-pagination__item {
    background: #1a1b23 !important;
    border-color: #2a2c3a;
    color: #e4e5eb;
    box-shadow: none;
  }
  &::v-deep .v-pagination__item--active {
    background: #ffd451 !important;
    border-color: #ffd451 !important;
    color: #1a1500 !important;
  }
  &::v-deep .v-pagination__navigation {
    background: #1a1b23 !important;
    border-color: #2a2c3a;
    box-shadow: none;

    .v-icon { color: #8b8d9e; }
  }
}

/* ══════ TABLE CARD ══════ */
.er-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  overflow: hidden;
}

.er-table {
  width: 100%;
  border-collapse: collapse;
}
.er-th {
  text-align: left;
  padding: 14px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
}
.er-row {
  transition: background .1s;
}
.er-row:hover { background: var(--card-hover); }
.er-row:not(:last-child) .er-td { border-bottom: 1px solid var(--border); }
.er-td {
  padding: 14px 16px;
  font-size: 13px;
  vertical-align: middle;
}
.er-td--num { color: var(--muted); font-variant-numeric: tabular-nums; }
.er-td--faculty { color: var(--text2); max-width: 200px; }
.er-td--date { color: var(--text2); font-variant-numeric: tabular-nums; font-size: 12px; }

/* Student cell */
.er-student { display: flex; align-items: center; gap: 12px; }
.er-student__avatar {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--accent), #f5c542);
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: #000;
  flex-shrink: 0;
}
.er-student__name { font-weight: 600; font-size: 13px; }
.er-student__phone { font-size: 11px; color: var(--muted); margin-top: 2px; }

/* Score */
.er-score { display: flex; align-items: baseline; gap: 1px; }
.er-score__val { font-weight: 700; font-size: 15px; font-variant-numeric: tabular-nums; }
.er-score__max { font-size: 12px; color: var(--muted); }
.er-bar {
  height: 4px; background: var(--border); border-radius: 2px;
  margin-top: 6px; overflow: hidden;
}
.er-bar__fill { height: 100%; border-radius: 2px; transition: width .4s ease; }
.er-bar--pass { background: var(--pass); }
.er-bar--fail { background: var(--fail); }

/* Badge */
.er-badge {
  display: inline-flex; align-items: center;
  padding: 4px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 600;
}
.er-badge--pass { background: var(--pass-bg); color: var(--pass); }
.er-badge--fail { background: var(--fail-bg); color: var(--fail); }
.er-badge--progress { background: var(--progress-bg); color: var(--progress); }
.er-score__val--na, .er-score-big__val--na { color: var(--muted); }

/* Actions */
.er-actions { display: flex; gap: 6px; }
.er-act {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); border-radius: 8px; cursor: pointer;
  background: none; color: var(--muted);
  transition: all .15s;
}
.er-act:hover { border-color: var(--accent); color: var(--accent); background: rgba(255,212,81,.06); }
.er-act--delete:hover { border-color: var(--fail); color: var(--fail); background: var(--fail-bg); }

/* ══════ SKELETON ══════ */
.er-skel {
  display: flex; gap: 20px; padding: 18px 16px;
  border-bottom: 1px solid var(--border);
}
.er-skel__bar {
  height: 14px; border-radius: 6px;
  background: linear-gradient(90deg, var(--border) 25%, rgba(0,0,0,.02) 50%, var(--border) 75%);
  background-size: 200% 100%;
  animation: er-shimmer 1.5s infinite;
}
@keyframes er-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Empty */
.er-empty {
  padding: 60px 20px;
  text-align: center; color: var(--muted);
}
.er-empty__title { font-size: 16px; font-weight: 600; margin-top: 12px; }
.er-empty__sub { font-size: 13px; margin-top: 4px; }

/* ══════ MODALS ══════ */
.er-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,.45);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 100;
  padding: 24px;
}
.er--dark .er-overlay { background: rgba(0,0,0,.65); }

.er-modal {
  background: var(--card);
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(0,0,0,.18);
  max-height: 85vh;
  display: flex; flex-direction: column;
  overflow: hidden;
}
.er-modal--detail { width: 900px; max-width: 100%; }
.er-modal--sm { width: 440px; max-width: 100%; }

.er-modal__header {
  display: flex; align-items: flex-start; gap: 16px;
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.er-modal__header-right {
  display: flex; align-items: center; gap: 8px;
  margin-left: auto; margin-right: 40px;
}
.er-modal__title { font-size: 18px; font-weight: 700; }
.er-modal__sub { font-size: 13px; color: var(--muted); margin-top: 4px; }
.er-modal__close {
  position: absolute; right: 20px; top: 20px;
  width: 36px; height: 36px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--border); border-radius: 10px;
  background: var(--card); color: var(--muted); cursor: pointer;
  transition: all .15s;
}
.er-modal__close:hover { color: var(--text); border-color: var(--muted); }
.er-modal__toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 14px;
}
.er-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
}
.er-print-btn:hover {
  background: var(--accent);
  color: #000;
  border-color: var(--accent);
}
.er-print-btn:active {
  transform: scale(.98);
}
.er-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--card);
  color: var(--muted);
  cursor: pointer;
  transition: all .15s;
}
.er-close-btn:hover {
  color: #fff;
  background: var(--fail);
  border-color: var(--fail);
}
.er-close-btn:active {
  transform: scale(.96);
}
.er-modal__body { padding: 24px 28px; overflow-y: auto; flex: 1; }
.er-modal__footer {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 28px 20px;
  border-top: 1px solid var(--border);
}
.er-modal__loading {
  display: flex; align-items: center; justify-content: center;
  padding: 60px;
}

/* Score big */
.er-score-big { display: flex; align-items: baseline; }
.er-score-big__val { font-size: 28px; font-weight: 800; letter-spacing: -.03em; }
.er-score-big__max { font-size: 16px; color: var(--muted); }
.er-detail-stat {
  font-size: 13px; color: var(--muted);
  padding: 4px 12px;
  background: var(--card-hover);
  border-radius: 8px;
}
.ml-3 { margin-left: 12px; }

/* Modal transitions */
.er-modal-enter-active, .er-modal-leave-active { transition: all .25s ease; }
.er-modal-enter, .er-modal-leave-to { opacity: 0; }
.er-modal-enter .er-modal, .er-modal-leave-to .er-modal { transform: scale(.96) translateY(10px); }

/* ══════ SCIENCE SUMMARY ══════ */
.er-science-summary {
  margin-bottom: 18px;
  padding: 16px 18px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
}
.er-science-summary__title {
  font-size: 13px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: .04em;
  margin-bottom: 12px;
}
.er-science-summary__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}
.er-science-card {
  padding: 12px 14px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card-hover);
}
.er-science-card__name {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--muted);
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.er-science-card__row {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
}
.er-science-card__correct {
  font-size: 20px;
  font-weight: 800;
  color: var(--text);
  letter-spacing: -.02em;
}
.er-science-card__sep,
.er-science-card__total {
  font-size: 14px;
  color: var(--muted);
}
.er-science-card__label {
  margin-left: 6px;
  font-size: 11.5px;
  color: var(--muted);
}
.er-science-card__score {
  display: flex;
  align-items: baseline;
  gap: 3px;
  font-size: 13px;
  color: var(--text);
}
.er-science-card__score-val {
  font-weight: 700;
}
.er-science-card__score-max {
  color: var(--muted);
  font-size: 12px;
}

/* ══════ QUESTION CARDS ══════ */
.er-q {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 16px 20px;
  margin-bottom: 12px;
  border-left: 4px solid var(--border);
  transition: all .15s;
}
.er-q--correct { border-left-color: var(--pass); }
.er-q--wrong { border-left-color: var(--fail); }
.er-q__head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 10px;
}
.er-q__num {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 8px; background: var(--card-hover);
  font-size: 12px; font-weight: 700; color: var(--text2);
}
.er-q__status { display: flex; }
.er-q--correct .er-q__status { color: var(--pass); }
.er-q--wrong .er-q__status { color: var(--fail); }
.er-q__science {
  font-size: 11px; color: var(--muted);
  padding: 3px 10px; background: var(--card-hover);
  border-radius: 6px; margin-left: auto;
}
.er-q__body { font-size: 14px; line-height: 1.6; margin-bottom: 12px; }
.er-q__answers { display: flex; flex-direction: column; gap: 6px; }

.er-a {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-radius: 8px;
  border: 1px solid var(--border);
  font-size: 13px; transition: all .1s;
}
.er-a--correct { border-color: var(--pass); background: var(--pass-bg); }
.er-a--wrong { border-color: var(--fail); background: var(--fail-bg); }
.er-a__letter {
  width: 24px; height: 24px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 6px; background: var(--card-hover);
  font-size: 11px; font-weight: 700; color: var(--text2);
  flex-shrink: 0;
}
.er-a--correct .er-a__letter { background: var(--pass); color: #fff; }
.er-a--wrong .er-a__letter { background: var(--fail); color: #fff; }
.er-a__text { flex: 1; }
.er-a__icon { margin-left: auto; flex-shrink: 0; }
.er-a__icon--correct { color: var(--pass); }
.er-a__icon--wrong { color: var(--fail); }

/* ══════ FORM CONTROLS ══════ */
.er-edit-name { font-weight: 600; margin-bottom: 20px; }
.er-field { display: block; margin-bottom: 20px; }
.er-field__label { display: block; font-size: 12px; font-weight: 600; color: var(--text2); margin-bottom: 6px; }
.er-field__input {
  width: 100%; padding: 10px 14px;
  border: 1px solid var(--border); border-radius: var(--radius-sm);
  background: var(--card); color: var(--text);
  font-size: 14px; outline: none; transition: border-color .15s;
}
.er-field__input:focus { border-color: var(--accent); }

/* Toggle */
.er-toggle { display: flex; align-items: center; gap: 12px; cursor: pointer; }
.er-toggle__input { display: none; }
.er-toggle__track {
  width: 44px; height: 24px;
  background: var(--border); border-radius: 12px;
  position: relative; transition: background .2s;
}
.er-toggle__thumb {
  position: absolute; top: 2px; left: 2px;
  width: 20px; height: 20px;
  background: #fff; border-radius: 50%;
  transition: transform .2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
}
.er-toggle__input:checked ~ .er-toggle__track {
  background: var(--accent);
}
.er-toggle__input:checked ~ .er-toggle__track .er-toggle__thumb {
  transform: translateX(20px);
}
.er-toggle__label { font-size: 14px; font-weight: 500; }

.er-caption { font-size: 13px; color: var(--muted); margin-top: 8px; }

/* ══════ BUTTONS ══════ */
.er-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 22px; border-radius: var(--radius-sm);
  font-size: 14px; font-weight: 600;
  border: none; cursor: pointer;
  transition: all .15s;
}
.er-btn:disabled { opacity: .5; cursor: default; }
.er-btn--primary {
  background: var(--accent); color: #000;
}
.er-btn--primary:hover:not(:disabled) { background: var(--accent-hover); }
.er-btn--ghost {
  background: transparent; color: var(--text);
  border: 1px solid var(--border);
}
.er-btn--ghost:hover:not(:disabled) { background: var(--card-hover); }
.er-btn--danger {
  background: var(--fail); color: #fff;
}
.er-btn--danger:hover:not(:disabled) { background: #b91c1c; }
/* Excel export button */
.er-export-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 10px;
  background: #217346; color: #fff;
  padding: 8px 20px; border-radius: 8px;
  border: none; cursor: pointer;
  font-size: 13px; font-weight: 600; letter-spacing: .03em;
  text-transform: none;
  transition: all .2s ease;
  height: 36px; min-width: 130px;
}
.er-export-btn img { width: 18px; height: 18px; filter: brightness(0) invert(1); margin-right: 2px; }
.er-export-btn:hover { background: #1a5c38; }
.er-export-btn:active { transform: scale(.97); }
.er-export-btn:disabled { opacity: .5; cursor: default; transform: none; }

/* ══════ SPINNER ══════ */
.er-spinner {
  width: 28px; height: 28px;
  border: 3px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: er-spin .6s linear infinite;
}
.er-spinner--sm { width: 16px; height: 16px; border-width: 2px; }
@keyframes er-spin { to { transform: rotate(360deg); } }

/* ══════ RESPONSIVE ══════ */
@media (max-width: 900px) {
  .er { padding: 0 12px 32px; }
  .er-nav {
    flex-wrap: wrap;
    margin: 0 -12px 16px;
    padding: 10px 12px;
    gap: 10px;
  }
  .er-nav__left { flex: 1 1 100%; gap: 8px; }
  .er-nav__right { flex: 1 1 100%; }
  .search-input { width: 100% !important; max-width: none !important; flex: 1; }
  .er-seg { min-width: 0; width: 100%; }
  .er-stats { flex-wrap: wrap; gap: 10px; }
  .er-stat-card { flex: 1 1 auto; padding: 12px 16px; }
  .er-stat-card__value { font-size: 18px; }

  /* Table horizontal scroll */
  .er-card { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .er-table { min-width: 700px; }
  .er-th, .er-td { padding: 10px 12px; font-size: 12px; }
  .er-student__avatar { width: 30px; height: 30px; font-size: 10px; }

  /* Modals */
  .er-overlay { padding: 12px; }
  .er-modal--detail { width: 100%; }
  .er-modal__header { padding: 16px 20px 14px; }
  .er-modal__header-right { flex-wrap: wrap; margin-right: 30px; gap: 6px; }
  .er-modal__body { padding: 16px 20px; }
  .er-modal__footer { padding: 12px 20px 16px; }
}

@media (max-width: 600px) {
  .er-nav__left { flex-direction: column; }
  .er-seg__btn { padding: 8px 10px; font-size: 12px; }

  .er-stats { flex-direction: column; }
  .er-stat-card { width: 100%; }
  .er-score-big__val { font-size: 22px; }
  .er-q { padding: 12px 14px; }
  .er-a { padding: 8px 10px; font-size: 12px; }
}
</style>

